import { Link, useNavigate } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { TargetClientSelect } from "@/components/copy-operations/TargetClientSelect";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useActiveBuInstances } from "@/hooks/use-bu-instances";
import type { CachedClient } from "@/hooks/use-cached-clients";
import { useCreateCopyOperation, usePriorCopyOperations } from "@/hooks/use-copy-operations";
import type { Contract } from "@/lib/coach-manager/types";
import { formatDateTime } from "@/lib/format";

interface InitiateCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceBuId: string;
  contract: Contract;
}

/**
 * "Initiate Copy" entry point from the Contract Search detail drawer: pick a
 * Target BU + Target Client, then create a staging draft and navigate
 * straight into the editable staging page. Unlike `CopyContractForm`'s
 * search-first flow, the source Contract is already known here (the drawer
 * is already showing it) — this dialog only needs the target side.
 */
export function InitiateCopyDialog({
  open,
  onOpenChange,
  sourceBuId,
  contract,
}: InitiateCopyDialogProps) {
  const navigate = useNavigate();
  const { label } = useBuInstanceLabel();
  const [targetBuId, setTargetBuId] = useState<string | null>(null);
  const [targetClientId, setTargetClientId] = useState<string | null>(null);
  const [targetClient, setTargetClient] = useState<CachedClient | null>(null);

  const { data: buInstances, isLoading: isLoadingBuInstances } = useActiveBuInstances();
  const createCopyOperation = useCreateCopyOperation();

  const priorAttempts = usePriorCopyOperations(
    targetBuId && targetClientId
      ? {
          sourceBuId,
          sourceObjectId: String(contract.ContractID),
          targetBuId,
          targetCustomerId: targetClientId,
        }
      : null,
  );
  const priorAttemptsList = priorAttempts.data ?? [];
  const mostRecentPriorAttempt = priorAttemptsList[0];

  function handleTargetBuChange(id: string) {
    setTargetBuId(id);
    setTargetClientId(null);
    setTargetClient(null);
    createCopyOperation.reset();
  }

  function handleClose(nextOpen: boolean) {
    if (!nextOpen) {
      setTargetBuId(null);
      setTargetClientId(null);
      setTargetClient(null);
      createCopyOperation.reset();
    }
    onOpenChange(nextOpen);
  }

  function handleStartCopy() {
    if (!targetBuId || !targetClientId) return;
    createCopyOperation.mutate(
      { sourceBuId, targetBuId, contractId: contract.ContractID, targetClientId },
      {
        onSuccess: (result) => {
          if (result.ok) {
            navigate({ to: "/dashboard/copy-operations/$id", params: { id: result.data.id } });
          }
        },
      },
    );
  }

  const canStart = Boolean(targetBuId && targetClientId) && !createCopyOperation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Initiate Copy</DialogTitle>
          <DialogDescription>
            Copy Contract #{contract.ContractID} to another {label}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Target {label}</p>
            <Select value={targetBuId ?? ""} onValueChange={handleTargetBuChange}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={isLoadingBuInstances ? "Loading…" : `Select a target ${label}`}
                />
              </SelectTrigger>
              <SelectContent>
                {(buInstances ?? [])
                  .filter((bu) => bu.id !== sourceBuId)
                  .map((bu) => (
                    <SelectItem key={bu.id} value={bu.id} disabled={!bu.companyId}>
                      {bu.name}
                      {!bu.companyId ? " (no Company ID configured)" : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Target Client</p>
              <Link
                to="/dashboard/client-search"
                className="text-xs text-primary underline-offset-4 hover:underline"
              >
                Can't find one? Search Coach Manager
              </Link>
            </div>
            <TargetClientSelect
              targetBuId={targetBuId}
              value={targetClientId}
              onChange={(clientId, client) => {
                setTargetClientId(clientId);
                setTargetClient(client);
              }}
              selectedLabel={
                targetClient
                  ? `${targetClient.company ?? targetClient.surname} (${targetClientId})`
                  : undefined
              }
            />
          </div>

          {createCopyOperation.error && <ErrorState message={createCopyOperation.error.message} />}
          {createCopyOperation.data && !createCopyOperation.data.ok && (
            <ErrorState message={createCopyOperation.data.error} />
          )}

          {mostRecentPriorAttempt && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                This contract has already been copied to this target {priorAttemptsList.length} time
                {priorAttemptsList.length === 1 ? "" : "s"} before — most recently{" "}
                <StatusBadge status={mostRecentPriorAttempt.status} /> on{" "}
                {formatDateTime(mostRecentPriorAttempt.initiatedAt)}
                {mostRecentPriorAttempt.resultObjectId &&
                  ` (Contract #${mostRecentPriorAttempt.resultObjectId})`}
                .
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canStart} onClick={handleStartCopy}>
            {createCopyOperation.isPending ? "Starting…" : "Start Copy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
